import { Injectable } from '@angular/core';

export interface PromotionEventData {
  promotion_id: string; // id de la promocion [OBLIGATORIO]
  location_id: string; // segun la posicion en la pantalla [OBLIGATORIO]
  promotion_name: string; // nombre de la promotion [OPCIONAL]
  creative_name: string; // se va a usar con las fechas. [OBLIGATORIO]
  creative_slot: string; // [OPCIONAL]
}

export interface ClickEventData {// para evento de click clasicos para los botones
  click_class: string;
  click_text: string;
}

export interface SectionTrackingData {
  section_name: string;
  section_id: string;
  is_visible: boolean;
  visibility_percentage: number;
  time_visible?: number;
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private trackedSections = new Set<string>();
  private trackedPromotions = new Set<string>(); // Para rastrear promociones ya vistas
  private sectionVisibilityTimes = new Map<string, number>();
  private intersectionObserver?: IntersectionObserver;
  private promotionObserver?: IntersectionObserver; // Observer específico para promociones

  constructor() {
    this.initializeIntersectionObserver();
    this.initializePromotionObserver();
  }

  /**
   * Track promotion selection event
   */
  trackPromotionSelection(eventData: PromotionEventData): void {
    this.sendToDataLayer('Select_promotion', eventData);
    this.sendToGoogleAnalytics('Select_promotion', eventData);
    console.log('[Analytics] Select_promotion:', eventData);
  }

  /**
   * Track promotion view event
   */
  trackPromotionView(eventData: PromotionEventData): void {
    this.sendToDataLayer('View_promotion', eventData);
    this.sendToGoogleAnalytics('View_promotion', eventData);
    console.log('[Analytics] View_promotion:', eventData);
  }

  /**
   * Send event to Google Analytics 4 (if gtag is available)
   */
  private sendToGoogleAnalytics(eventName: string, parameters: PromotionEventData | SectionTrackingData | any): void {
    if (typeof gtag !== 'undefined') {
      gtag('event', eventName, parameters);
    }
  }

  /**
   * Send event to Google Tag Manager dataLayer
   * This is the primary method for GTM integration
   */
  private sendToDataLayer(eventName: string, parameters: PromotionEventData | SectionTrackingData | any): void {
    if (typeof window !== 'undefined' && window.dataLayer) {
      let eventPayload: any = {
        event: eventName,
        ...parameters
      };

      // Agregar campos específicos según el tipo de evento
      if (eventName.includes('promotion')) {
        eventPayload = {
          ...eventPayload,
          event_category: 'Promotions',
          event_action: eventName,
          event_label: (parameters as PromotionEventData).promotion_name
        };
      } else if (eventName.includes('Section')) {
        eventPayload = {
          ...eventPayload,
          event_category: 'Sections',
          event_action: eventName,
          event_label: (parameters as SectionTrackingData).section_name
        };
      }

      window.dataLayer.push(eventPayload);
    }
  }

  /**
   * Create promotion event data object
   */
  createPromotionEventData(
    promotionId: number | string,
    locationId: string,
    promotionName: string,
    creativeName: string,
    creativeSlot: string
  ): PromotionEventData {
    return {
      promotion_id: promotionId.toString(),
      location_id: locationId,
      promotion_name: promotionName,
      creative_name: creativeName,
      creative_slot: creativeSlot
    };
  }

  /**
   * Track page view (useful for GTM)
   */
  trackPageView(pageTitle?: string, pagePath?: string): void {
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'page_view',
        page_title: pageTitle || document.title,
        page_path: pagePath || window.location.pathname
      });
    }
  }

  /**
   * Track custom event
   */
  trackCustomEvent(eventName: string, parameters: any = {}): void {
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: eventName,
        ...parameters
      });
    }
  }

  /**
   * Track button click events
   */
  trackButtonClick(buttonName: string, buttonLocation: string, additionalData: any = {}): void {
    if (typeof window !== 'undefined' && window.dataLayer) {
      const deviceType = this.getCurrentDeviceType();

      window.dataLayer.push({
        event: 'button_click',
        event_category: 'Engagement',
        event_action: 'Click',
        event_label: buttonName,
        button_name: buttonName,
        button_location: buttonLocation,
        device_type: deviceType,
        page_path: window.location.pathname,
        ...additionalData
      });

      console.log(`[Analytics] Button Click: ${buttonName} in ${buttonLocation}`);
    }
  }

  /**
   * Track link click events
   */
  trackLinkClick(linkText: string, linkUrl: string, linkLocation: string, isExternal: boolean = false): void {
    if (typeof window !== 'undefined' && window.dataLayer) {
      const deviceType = this.getCurrentDeviceType();

      window.dataLayer.push({
        event: 'link_click',
        event_category: 'Navigation',
        event_action: 'Click',
        event_label: linkText,
        link_text: linkText,
        link_url: linkUrl,
        link_location: linkLocation,
        is_external: isExternal,
        device_type: deviceType,
        page_path: window.location.pathname
      });

      console.log(`[Analytics] Link Click: ${linkText} (${linkUrl}) in ${linkLocation}`);
    }
  }

  /**
   * Track CTA (Call to Action) click events
   */
  trackCTAClick(ctaText: string, ctaLocation: string, ctaType: string = 'button', additionalData: any = {}): void {
    if (typeof window !== 'undefined' && window.dataLayer) {
      const deviceType = this.getCurrentDeviceType();

      window.dataLayer.push({
        event: 'cta_click',
        event_category: 'Conversion',
        event_action: 'Click',
        event_label: ctaText,
        cta_text: ctaText,
        cta_location: ctaLocation,
        cta_type: ctaType,
        device_type: deviceType,
        page_path: window.location.pathname,
        ...additionalData
      });

      console.log(`[Analytics] CTA Click: ${ctaText} in ${ctaLocation}`);
    }
  }

  /**
   * Get current device type based on window width
   */
  getCurrentDeviceType(): 'xl' | 'desktop' | 'tablet' | 'movil' {
    if (typeof window === 'undefined') return 'desktop';

    const width = window.innerWidth;

    // Breakpoints más precisos
    if (width >= 1281) return 'xl';        // 1280px y superior
    if (width >= 1024) return 'desktop';   // 1024px - 1279px
    if (width >= 768) return 'tablet';     // 768px - 1023px
    return 'movil';                        // Menos de 768px
  }

  /**
   * Initialize Intersection Observer for section visibility tracking
   */
  private initializeIntersectionObserver(): void {
    if (typeof window === 'undefined') return;

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const sectionId = entry.target.getAttribute('data-section-id');
          if (!sectionId) return;

          const visibilityPercentage = Math.round(entry.intersectionRatio * 100);
          const isVisible = entry.isIntersecting && visibilityPercentage >= 50; // Solo si está 50% visible

          if (isVisible && !this.trackedSections.has(sectionId)) {
            // Sección se vuelve visible por primera vez
            this.trackedSections.add(sectionId);
            this.sectionVisibilityTimes.set(sectionId, Date.now());

            this.trackSectionView(sectionId, entry.target.getAttribute('data-section-name') || sectionId);
            console.log(`[Analytics] Section visible: ${sectionId} (${visibilityPercentage}%)`);
          }
          // Removido el tracking de Section_exit - solo queremos Section_view
        });
      },
      {
        threshold: [0, 0.25, 0.5, 0.75, 1.0], // Múltiples umbrales para mejor detección
        rootMargin: '0px 0px -10% 0px' // Solo cuenta si está en la parte superior del viewport
      }
    );
  }

  /**
   * Initialize Intersection Observer for promotion lazy loading
   */
  private initializePromotionObserver(): void {
    if (typeof window === 'undefined') return;

    this.promotionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const promotionId = entry.target.getAttribute('data-promotion-id');
          if (!promotionId) return;

          const visibilityPercentage = Math.round(entry.intersectionRatio * 100);
          const isVisible = entry.isIntersecting && visibilityPercentage >= 50; // 50% visible para promociones

          if (isVisible && !this.trackedPromotions.has(promotionId)) {
            // Promoción se vuelve visible por primera vez
            this.trackedPromotions.add(promotionId);

            // Obtener datos de la promoción desde el elemento
            const promotionName = entry.target.getAttribute('data-promotion-name') || '';
            const creativeName = entry.target.getAttribute('data-creative-name') || '';
            const creativeSlot = entry.target.getAttribute('data-creative-slot') || '';
            const locationId = this.getDeviceType();

            const eventData = this.createPromotionEventData(
              promotionId,
              locationId,
              promotionName,
              creativeName,
              creativeSlot
            );

            this.trackPromotionView(eventData);
            console.log(`[Analytics] Promotion visible: ${promotionId} (${visibilityPercentage}%) - ${promotionName}`);
          }
        });
      },
      {
        threshold: [0.3, 0.5, 0.7, 1.0], // Solo umbrales altos para promociones
        rootMargin: '0px 0px 0px 0px' // Sin margen, solo cuando esté realmente visible
      }
    );
  }

  /**
   * Get device type for location_id
   */
  private getDeviceType(): string {
    if (typeof window === 'undefined') return 'desktop';

    const width = window.innerWidth;
    if (width >= 1281) return 'xl';
    if (width >= 1024) return 'desktop';
    if (width >= 768) return 'tablet';
    return 'movil';
  }

  /**
   * Register a section for visibility tracking
   */
  registerSectionForTracking(element: HTMLElement, sectionName: string, sectionId: string): void {
    if (!this.intersectionObserver) return;

    element.setAttribute('data-section-id', sectionId);
    element.setAttribute('data-section-name', sectionName);
    this.intersectionObserver.observe(element);
  }

  /**
   * Unregister a section from visibility tracking
   */
  unregisterSectionFromTracking(element: HTMLElement): void {
    if (!this.intersectionObserver) return;
    this.intersectionObserver.unobserve(element);
  }

  /**
   * Track section view event (only when section becomes visible)
   */
  private trackSectionView(sectionId: string, sectionName: string): void {
    const eventData: SectionTrackingData = {
      section_name: sectionName,
      section_id: sectionId,
      is_visible: true,
      visibility_percentage: 100
    };

    this.sendToDataLayer('Section_view', eventData);
    this.sendToGoogleAnalytics('Section_view', eventData);
    console.log('[Analytics] Section_view:', eventData);
  }

  /**
   * Track section exit event
   */
  private trackSectionExit(sectionId: string, timeVisible: number): void {
    const eventData: SectionTrackingData = {
      section_name: sectionId,
      section_id: sectionId,
      is_visible: false,
      visibility_percentage: 0,
      time_visible: timeVisible
    };

    this.sendToDataLayer('Section_exit', eventData);
    this.sendToGoogleAnalytics('Section_exit', eventData);
    console.log('[Analytics] Section_exit:', eventData);
  }

  /**
   * Track promotion view only if section is visible
   */
  trackPromotionViewIfVisible(eventData: PromotionEventData, sectionElement: HTMLElement): void {
    const sectionId = sectionElement.getAttribute('data-section-id');
    if (sectionId && this.trackedSections.has(sectionId)) {
      this.trackPromotionView(eventData);
    }
  }

  /**
   * Register a promotion for lazy loading tracking
   */
  registerPromotionForTracking(
    element: HTMLElement,
    promotionId: string,
    promotionName: string,
    creativeName: string,
    creativeSlot: string
  ): void {
    if (!this.promotionObserver) return;

    element.setAttribute('data-promotion-id', promotionId);
    element.setAttribute('data-promotion-name', promotionName);
    element.setAttribute('data-creative-name', creativeName);
    element.setAttribute('data-creative-slot', creativeSlot);
    this.promotionObserver.observe(element);
  }

  /**
   * Unregister a promotion from tracking
   */
  unregisterPromotionFromTracking(element: HTMLElement): void {
    if (!this.promotionObserver) return;
    this.promotionObserver.unobserve(element);
  }

  /**
   * Clean up intersection observers
   */
  ngOnDestroy(): void {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    if (this.promotionObserver) {
      this.promotionObserver.disconnect();
    }
  }
}
